import React, { useContext, useState } from 'react';
import {
  Box,
  CardMedia,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/HighlightOff';
import { ContentCopy, InsertDriveFile, LibraryAdd } from '@mui/icons-material';
import FileUploadApi from './FileUpload.api';
import LoggingService from '../shared/Logging.api';
import SpinningSpark from '../shared/components/SpinningSpark';
import { AlertsContext } from '../shared/alerts/Alerts.context';

const MAX_IMAGE_SIZE_IN_MB = 8;
const MAX_VIDEO_SIZE_IN_MB = 500;

export default function FileUpload() {
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const { showAlert } = useContext(AlertsContext);

  const uploadFile = async (selectedFile) => {
    if (!selectedFile) return;

    const isImage = selectedFile.type.startsWith('image');
    const maxFileSize = isImage ? MAX_IMAGE_SIZE_IN_MB : MAX_VIDEO_SIZE_IN_MB;

    if (selectedFile.size > maxFileSize * 1024 * 1024) {
      showAlert(
        `${
          isImage ? 'Images' : 'Videos'
        } must be smaller than ${maxFileSize} MB`,
        'error'
      );
      return;
    }

    setLoading(true);
    setFile(selectedFile);
    setFileUrl('');
    try {
      const url = await FileUploadApi.uploadFile(selectedFile);
      setFileUrl(url);
      showAlert('File uploaded successfully', 'success');
    } catch (error) {
      LoggingService.error('Error uploading file', error);
      showAlert('Failed to upload file', 'error');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileUrl('');
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(fileUrl);
      showAlert('Link copied to clipboard', 'success');
    } catch (error) {
      showAlert('Failed to copy link', 'error');
    }
  };

  let mediaComponent = null;
  if (file?.type.startsWith('image')) {
    mediaComponent = 'img';
  } else if (file?.type.startsWith('video')) {
    mediaComponent = 'video';
  }

  let filePreview = null;
  if (mediaComponent) {
    filePreview = (
      <CardMedia
        src={fileUrl}
        component={mediaComponent}
        controls
        sx={styles.media}
      />
    );
  } else if (file) {
    filePreview = (
      <>
        <InsertDriveFile sx={styles.uploadIcon} />
        <Typography sx={styles.fileName}>{file.name}</Typography>
      </>
    );
  }

  let uploadAreaContent;
  if (loading) {
    uploadAreaContent = <SpinningSpark marginTop="0" />;
  } else if (fileUrl) {
    uploadAreaContent = (
      <>
        {filePreview}
        <IconButton onClick={clearFile} sx={styles.clearButton}>
          <ClearIcon titleAccess="Remove file" sx={styles.clearIcon} />
        </IconButton>
      </>
    );
  } else {
    uploadAreaContent = (
      <>
        <LibraryAdd sx={styles.uploadIcon} />
        <Typography sx={styles.uploadText}>
          Add a file
          <br />
          to get a CDN link
        </Typography>
        <VisuallyHiddenInput
          type="file"
          onChange={(event) => {
            uploadFile(event.target.files[0]);
            // eslint-disable-next-line no-param-reassign
            event.target.value = '';
          }}
        />
      </>
    );
  }

  const isPickable = !loading && !fileUrl;

  return (
    <Stack spacing={4} paddingTop={3}>
      <Typography variant="h4">File Upload</Typography>

      <Box sx={styles.container}>
        <Box
          component={isPickable ? 'label' : 'div'}
          sx={[styles.uploadArea, isPickable && styles.uploadAreaPickable]}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (isPickable) uploadFile(event.dataTransfer.files[0]);
          }}
        >
          {uploadAreaContent}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={styles.helpText}>
          A quick way to get a shareable CDN link for a photo or video that you
          can later link in a CTA or use elsewhere. Upload a file, then copy the
          link below it.
        </Typography>

        {!loading && fileUrl && (
          <TextField
            fullWidth
            value={fileUrl}
            sx={styles.linkField}
            onFocus={(event) => event.target.select()}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy link">
                    <IconButton onClick={copyUrl} edge="end">
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>
    </Stack>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const styles = {
  container: {
    width: '100%',
    maxWidth: '683px',
    alignSelf: 'center',
  },
  uploadArea: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    padding: 2,
    borderRadius: 2,
    backgroundColor: '#F6F9FC',
  },
  uploadAreaPickable: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#EDF2F7' },
  },
  uploadIcon: {
    fontSize: 40,
    color: '#4A4A4A',
    marginBottom: 1,
  },
  uploadText: {
    color: '#74767C',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  media: {
    maxWidth: '100%',
    maxHeight: 400,
    width: 'auto',
    borderRadius: 2,
  },
  fileName: {
    fontWeight: 600,
    textAlign: 'center',
    wordBreak: 'break-all',
  },
  clearButton: {
    position: 'absolute',
    padding: 0,
    top: 8,
    right: 8,
  },
  clearIcon: {
    width: 25,
    height: 25,
    borderRadius: '50%',
    backgroundColor: 'white',
  },
  helpText: {
    marginTop: 1,
  },
  linkField: {
    marginTop: 2,
    borderRadius: 2,
    backgroundColor: '#F6F9FC',
    '& fieldset': { border: 'none' },
  },
};
